import {
    Box,
    Dialog,
    DialogContent,
    Icon,
    IconButton,
    InputAdornment,
    TextField,
    Tabs,
    Typography,
    Tab,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TabPanel } from 'app/components/tab-panel';
import { Translate } from 'app/components/translate';
import { Store } from 'app/model';
import { MidFilter } from 'app/model/mid-filter';
import { storeFiltersService } from 'app/services/store-filters.service';

import {
    deleteFilter,
    fetchFilters,
    saveFilter,
    selectMidFilters,
    selectSavedFilters,
    selectStoreFilters,
    setActiveFilters,
    setStoreFilters,
    saveStoreFilter,
} from '../tuple.slice';

interface FilterModalProps {
    open: boolean;
    onClose: () => void;
    store: Store | undefined;
}

export const FilterModal: React.FunctionComponent<FilterModalProps> = (props) => {
    const { open, onClose, store } = props;
    const dispatch = useDispatch();
    const [currentTab, setCurrentTab] = useState(0);
    useEffect(() => {
        dispatch(fetchFilters(store));
    }, [dispatch, store]);

    const filterData = useSelector(selectSavedFilters);
    const activeFilter = useSelector(selectMidFilters);
    const storeData = useSelector(selectStoreFilters);

    const filterFormatter = (fieldData: MidFilter, key: number) => (
        <li key={key}>
            {fieldData.field}: <strong>{fieldData.value}</strong> {fieldData.negate && '(NOT)'}
        </li>
    );

    const handleSaveFilter = (): void => {
        if (0 === currentTab && store) {
            dispatch(saveStoreFilter(activeFilter, store));

            return;
        }
        dispatch(saveFilter(activeFilter));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogContent>
                <Typography variant='h5'>
                    <Translate tkey={'headings:Current Filter'} />
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {
                            xs: 'column',
                            sm: 'row',
                        },
                        alignItems: {
                            xs: 'stretch',
                            sm: 'center',
                        },
                    }}
                >
                    <TextField
                        name='name'
                        type='text'
                        size='small'
                        label='Name'
                        variant='outlined'
                        value={activeFilter.name}
                        onChange={(evt) => {
                            dispatch(setActiveFilters({ ...activeFilter, name: evt.target.value }));
                        }}
                        helperText={<Translate tkey='patterns.filter.name.helperText' />}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <IconButton edge='end' color='primary' onClick={handleSaveFilter}>
                                        <Icon>save</Icon>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {0 < activeFilter.filters.length ? (
                        <ul>{activeFilter.filters.map(filterFormatter)}</ul>
                    ) : (
                        <Typography variant='body2' m={1}>
                            [<Translate tkey='patterns.filter.emptyFilter' />]
                        </Typography>
                    )}
                </Box>

                <Tabs
                    value={currentTab}
                    onChange={(evt, tab) => {
                        setCurrentTab(tab as number);
                    }}
                    indicatorColor='primary'
                    textColor='primary'
                    centered
                >
                    <Tab label='Store Filters' />
                    <Tab label='My Filters' />
                </Tabs>
                <Typography variant='h5'>
                    <Translate tkey={'headings:Saved Filters'} />
                </Typography>
                <TabPanel value={currentTab} index={0}>
                    {(!storeData || 0 === storeData.length) && <p>[None]</p>}
                    {storeData?.map((filters, key) => (
                        <div key={key}>
                            <hr />
                            <Typography variant='h6' color={filters.name === activeFilter.name ? 'warning.main' : ''}>
                                {filters.name}
                                <IconButton
                                    size='small'
                                    color='info'
                                    onClick={() => {
                                        onClose();
                                        dispatch(setActiveFilters(filters));
                                    }}
                                >
                                    <Icon>filter_alt</Icon>
                                </IconButton>
                                {store && (
                                    <IconButton
                                        size='small'
                                        color='error'
                                        onClick={() => {
                                            storeFiltersService.deleteFilter(filters, store).subscribe(() => {
                                                dispatch(setStoreFilters(storeData.filter((f) => f.id !== filters.id)));
                                            });
                                        }}
                                    >
                                        <Icon>delete</Icon>
                                    </IconButton>
                                )}
                            </Typography>
                            {filters.filters.map(filterFormatter)}
                        </div>
                    ))}
                </TabPanel>

                <TabPanel value={currentTab} index={1}>
                    {(!filterData || 0 === filterData.data.length) && <p>[None]</p>}
                    {filterData?.data.map((filters, key) => (
                        <div key={key}>
                            <hr />
                            <Typography variant='h6' color={filters.name === activeFilter.name ? 'warning.main' : ''}>
                                {filters.name}
                                <IconButton
                                    size='small'
                                    color='info'
                                    onClick={() => {
                                        onClose();
                                        dispatch(setActiveFilters(filters));
                                    }}
                                >
                                    <Icon>filter_alt</Icon>
                                </IconButton>
                                <IconButton
                                    size='small'
                                    color='error'
                                    onClick={() => {
                                        dispatch(deleteFilter(filters));
                                    }}
                                >
                                    <Icon>delete</Icon>
                                </IconButton>
                            </Typography>
                            {filters.filters.map(filterFormatter)}
                        </div>
                    ))}
                </TabPanel>
            </DialogContent>
        </Dialog>
    );
};
